import type { UserResource } from '@clerk/nextjs/types';

import { portfolioClaimSignUpMetadata } from '@/features/portfolio/_utils/portfolio-claim-origin';
import type { resolvePortfolioAttribution } from '@/features/portfolio/_utils/resolve-portfolio-attribution';
import type { FeatureFlagVariant } from '@/lib/feature-flags/types';

export interface SignUpMirrorMetadataInput {
  freemiumVariant?: FeatureFlagVariant;
  oneTapVariant?: FeatureFlagVariant;
  partner?: string | null;
  portfolioAttribution?: ReturnType<typeof resolvePortfolioAttribution>;
  utmContent?: string | null;
}

/** The part of a Clerk user that the mirror touches. `clerk.user` satisfies it; so does a test double. */
export interface MirrorableClerkUser {
  unsafeMetadata: UserResource['unsafeMetadata'];
  update: (params: { unsafeMetadata: UserResource['unsafeMetadata'] }) => Promise<unknown>;
}

/**
 * The attribution a new Google account carries onto its Clerk `unsafeMetadata`, so the backend and the
 * analytics profile read the same arms and partner the sign-up screen saw. Every Google sign-up path
 * (redirect callback, One Tap) builds its payload here. Pure: what goes in is what comes out, nothing else.
 */
export function buildSignUpMirrorMetadata(input: SignUpMirrorMetadataInput): Record<string, unknown> {
  const mirrored: Record<string, unknown> = { ...portfolioClaimSignUpMetadata(input.utmContent) };
  if (input.partner) {
    mirrored.partner = input.partner;
  }
  if (input.portfolioAttribution) {
    mirrored.portfolio_attribution = input.portfolioAttribution;
  }
  if (input.freemiumVariant !== undefined) {
    mirrored.ab_test_freemium = input.freemiumVariant;
  }
  if (input.oneTapVariant !== undefined) {
    mirrored.ab_test_google_one_tap_2026_q4 = input.oneTapVariant;
  }
  return mirrored;
}

/**
 * Writes the mirrored attribution onto the user, keeping whatever metadata is already there. A failed
 * write is reported through `onError` and never thrown: provisioning must still run, because an account
 * that exists in Clerk and was never provisioned is the failure that matters.
 */
export async function mirrorSignUpMetadataToClerk(
  user: MirrorableClerkUser | null | undefined,
  mirrored: Record<string, unknown>,
  onError: (errorMessage: string) => void,
): Promise<void> {
  if (Object.keys(mirrored).length === 0 || !user) {
    return;
  }

  try {
    await user.update({
      unsafeMetadata: {
        ...user.unsafeMetadata,
        ...mirrored,
      },
    });
  } catch (error) {
    onError(error instanceof Error ? error.message : String(error));
  }
}
